import { Box, Checkbox, FormControlLabel, FormHelperText, Typography } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { AlertConfigurationForm } from "../../schemas/alertConfigurationSchema";
import { useLingui } from "@lingui/react";
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SmsIcon from '@mui/icons-material/Sms';
import EmailIcon from '@mui/icons-material/Email';
import { useEffect, useMemo, useState } from "react";
import AmaruIcon from "@/icons/AmaruIcon";

export interface UserNotificationPreference {
  userId: string;
  useWhatsapp: boolean;
  useEmail: boolean;
  useSms: boolean;
  useAmaruLite: boolean;
}

interface User {
  id: number;
  name: string;
  email: string;
  useWhatsapp: boolean;
  useEmail: boolean;
  useSms: boolean;
  usePush: boolean;
}
interface UserSelectionStepProps {
  users: User[];
  onPreferencesChange: (original: Map<string, UserNotificationPreference>) => void;
}

const UserSelectionStep = ({ users, onPreferencesChange }: UserSelectionStepProps) => {
  const { formState: { errors }, setValue, watch } = useFormContext<AlertConfigurationForm>();

  const usersToNotify = watch('usersToNotify') || [];

  const originalPreferences = useMemo(() => {
    const prefs = new Map<string, UserNotificationPreference>();
    users.forEach(user => {
      prefs.set(user.id.toString(), {
        userId: user.id.toString(),
        useEmail: user.useEmail ?? false,
        useSms: user.useSms ?? false,
        useWhatsapp: user.useWhatsapp ?? false,
        useAmaruLite: user.usePush ?? false,
      });
    });
    return prefs;
  }, [users])


  const [currentPreferences, setCurrentPreferences] = useState<Map<string, UserNotificationPreference>>(() => {
    if (usersToNotify.length > 0) {
      const prefs = new Map<string, UserNotificationPreference>();
      users.forEach(user => {
        prefs.set(user.id.toString(), {
          userId: user.id.toString(),
          useEmail: user.useEmail ?? false,
          useSms: user.useSms ?? false,
          useWhatsapp: user.useWhatsapp ?? false,
          useAmaruLite: user.usePush ?? false,
        });
      });

      usersToNotify.forEach(userPref => {
        prefs.set(userPref.userId, {
          userId: userPref.userId,
          useEmail: userPref.useEmail,
          useSms: userPref.useSms,
          useWhatsapp: userPref.useWhatsapp,
          useAmaruLite: userPref.useAmaruLite,
        });
      });
      return prefs;
    }

    const prefs = new Map<string, UserNotificationPreference>();
    users.forEach(user => {
      prefs.set(user.id.toString(), {
        userId: user.id.toString(),
        useEmail: user.useEmail ?? false,
        useSms: user.useSms ?? false,
        useWhatsapp: user.useWhatsapp ?? false,
        useAmaruLite: user.usePush ?? false,
      });
    });
    return prefs;

  });


  const handleChannelChange = (userId: string, channel: string, value: boolean) => {
    setCurrentPreferences(prev => {
      const newPreferences = new Map(prev);
      const userPref = newPreferences.get(userId);

      if (userPref) {
        newPreferences.set(userId, {
          ...userPref,
          [channel]: value,
        });
      }
      return newPreferences;
    });
  };

  const handleSelectAllChannel = (channel: keyof Omit<UserNotificationPreference, 'userId'>, value: boolean) => {
    setCurrentPreferences(prev => {
      const newPreferences = new Map(prev);

      users.forEach(user => {
        const userPref = newPreferences.get(user.id.toString());
        if (userPref) {
          newPreferences.set(user.id.toString(), {
            ...userPref,
            [channel]: value,
          });
        }
      });

      return newPreferences;
    })
  }

    // ✅ Calcular estado de cada checkbox maestro (todos/algunos/ninguno)
  const getChannelState = (channel: keyof Omit<UserNotificationPreference, 'userId'>) => {
    const allUsers = Array.from(currentPreferences.values());
    const selectedCount = allUsers.filter(pref => pref[channel]).length;
    
    return {
      checked: selectedCount === allUsers.length,
      indeterminate: selectedCount > 0 && selectedCount < allUsers.length,
    };
  };

  const hasUserChanges = (userId: string): boolean => {
    const current = currentPreferences.get(userId);
    const original = originalPreferences.get(userId);

    return (
      current?.useAmaruLite !== original?.useAmaruLite ||
      current?.useEmail !== original?.useEmail ||
      current?.useSms !== original?.useSms ||
      current?.useWhatsapp !== original?.useWhatsapp
    );
  }

  useEffect(() => {
    const modifiedUsers: UserNotificationPreference[] = [];
    
    currentPreferences.forEach((current, userId) => {
      if (hasUserChanges(userId)) {
        modifiedUsers.push(current);
      }
    });

    setValue('usersToNotify', modifiedUsers);
  }, [currentPreferences])

  useEffect(() => {
    if (currentPreferences.size > 0) onPreferencesChange(currentPreferences);
  }, [currentPreferences])
  

  if (currentPreferences.size === 0) {
    return (
      <Typography variant="body2">
        No hay usuarios disponibles para notificar. Por favor, añade usuarios al dispositivo primero.
      </Typography>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant='h5'>
          Define los usuarios y medios por el cual se les notificará
        </Typography>
      </Box>

      <Box sx={{ mb: 1 }}>
        <ColumnNameRow />
      </Box>

      {/* ✅ Fila de checkboxes maestros */}
      <Box 
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 2,
          mb: 1,
          p: 1,
          backgroundColor: '#2A3541',
          borderRadius: 1,
        }}
      >
        <Box sx={{ width: { sm: '33%' } }} />
        <Box sx={{ width: { sm: '33%' } }} />
        <Box sx={{ 
          width: { sm: '33%' },
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          px: 2,
        }}>
          <MasterChannelCheckbox
            icon={<WhatsAppIcon fontSize="small" />}
            channel="useWhatsapp"
            state={getChannelState('useWhatsapp')}
            onToggle={handleSelectAllChannel}
          />
          <MasterChannelCheckbox
            icon={<SmsIcon fontSize="small" />}
            channel="useSms"
            state={getChannelState('useSms')}
            onToggle={handleSelectAllChannel}
          />
          <MasterChannelCheckbox
            icon={<EmailIcon fontSize="small" />}
            channel="useEmail"
            state={getChannelState('useEmail')}
            onToggle={handleSelectAllChannel}
          />
          <MasterChannelCheckbox
            icon={<AmaruIcon fontSize="small" />}
            channel="useAmaruLite"
            state={getChannelState('useAmaruLite')}
            onToggle={handleSelectAllChannel}
          />
        </Box>
      </Box>


      <Box 
        sx={{
          height: '50vh',
          overflowY: 'scroll',
          backgroundColor: '#1E262F',
          borderRadius: 2
        }} 
      >
        {users.map((user, index) => (
          <ContactRow
            key={user.id}
            index={index}
            user={user}
            currentPreference={currentPreferences.get(user.id.toString())!}
            originalPreference={originalPreferences.get(user.id.toString())!}
            onChannelChange={handleChannelChange}
          />
        ))}
      </Box>

      {errors.usersToNotify && !Array.isArray(errors.usersToNotify) && (
        <FormHelperText error sx={{ textAlign: 'center' }}>
          {errors.usersToNotify.message}
        </FormHelperText>        
      )}

    </Box>
  );
};


interface MasterChannelCheckboxProps {
  icon: React.ReactNode;
  channel: keyof Omit<UserNotificationPreference, 'userId'>;
  state: {
    checked: boolean;
    indeterminate: boolean;
  };
  onToggle: (channel: keyof Omit<UserNotificationPreference, 'userId'>, value: boolean) => void;
}

const MasterChannelCheckbox = ({ icon, channel, state, onToggle }: MasterChannelCheckboxProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
      }}
    >
      {icon}
      <Checkbox
        checked={state.checked}
        indeterminate={state.indeterminate}
        onChange={(e) => onToggle(channel, e.target.checked)}
        size="small"
        sx={{
          p: 0,
          '&.MuiCheckbox-indeterminate': {
            color: 'warning.main',
          },
        }}
      />
    </Box>
  );
};


interface ContactRowProps {
  index: number;
  user: User;
  currentPreference: UserNotificationPreference;
  originalPreference: UserNotificationPreference;
  onChannelChange: (userId: string, channel: string, value: boolean) => void;
}

const ContactRow = ({user, currentPreference, originalPreference, onChannelChange}: ContactRowProps) => {

  const hasChanges = (
    currentPreference.useAmaruLite !== originalPreference.useAmaruLite ||
    currentPreference.useEmail !== originalPreference.useEmail ||
    currentPreference.useSms !== originalPreference.useSms ||
    currentPreference.useWhatsapp !== originalPreference.useWhatsapp
  );

  return (
    <Box key={user.id}         
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 2,
        bgcolor: hasChanges ? 'action.hover' : 'transparent',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', width: { sm: '33%' } }}>
        <Typography sx={{ p: 1 }}>{user.name}</Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'center', width: { sm: '33%' } }}>
        <Typography sx={{ p: 2 }}>{user.email}</Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'center', width: { sm: '33%' } }}>
        <ContactChannelSelector
          user={user}
          currentPreference={currentPreference}
          onChannelChange={onChannelChange}
        />
      </Box>
    </Box>
  );
}

const ContactChannelSelector = ({
  user,
  currentPreference,
  onChannelChange,
}: {
  user: User;
  currentPreference: UserNotificationPreference;
  onChannelChange: (userId: string, channel: string, value: boolean) => void;
}) => {
  return (
    <Box sx={{ width: '90%', display: 'flex', justifyContent: 'space-between' }}>
      <FormControlLabel
        sx={{
          '& .MuiFormControlLabel-label': {
            display: 'flex',
            alignItems: 'center'
          }
        }}
        labelPlacement='start'
        label={<WhatsAppIcon/>}
        control={
          <Checkbox
            checked={currentPreference.useWhatsapp}
            onChange={(e) => onChannelChange(user.id.toString(), 'useWhatsapp', e.target.checked)}
          />
        }
      />
      <FormControlLabel
        sx={{
          '& .MuiFormControlLabel-label': {
            display: 'flex',
            alignItems: 'center'
          }
        }}          
        labelPlacement='start'
        label={<SmsIcon/>}
        control={
          <Checkbox
            checked={currentPreference.useSms}
            onChange={(e) => onChannelChange(user.id.toString(), 'useSms', e.target.checked)}
          />
        }
      />
      <FormControlLabel
        sx={{
          '& .MuiFormControlLabel-label': {
            display: 'flex',
            alignItems: 'center'
          }
        }}          
        labelPlacement='start'
        label={<EmailIcon/>}
        control={
          <Checkbox
            checked={currentPreference.useEmail}
            onChange={(e) => onChannelChange(user.id.toString(), 'useEmail', e.target.checked)}
          />
        }
      />
      <FormControlLabel
        sx={{
          '& .MuiFormControlLabel-label': {
            display: 'flex',
            alignItems: 'center'
          }
        }}          
        labelPlacement='start'
        label={<AmaruIcon/>}
        control={
          <Checkbox
            checked={currentPreference.useAmaruLite}
            onChange={(e) => onChannelChange(user.id.toString(), 'useAmaruLite', e.target.checked)}      
          />
        }
      />
    </Box>
  );
}

const ColumnNameRow = () => {
  const { _ } = useLingui();

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          border: '1px solid gray',
          borderRadius: 2,
          width: { sm: '33%' }
        }}
      >
        <Typography sx={{ p: 2 }}>Nombre</Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          border: '1px solid gray',
          borderRadius: 2,
          width: { sm: '33%' }
        }}
      >
        <Typography sx={{ p: 2 }}>Dirección de correo</Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          border: '1px solid gray',
          borderRadius: 2,
          width: { sm: '33%' }
        }}
      >
        <Typography sx={{ p: 2 }}>Canales de notificación</Typography>
      </Box>
    </Box>
  );
}

export default UserSelectionStep;